import React, {
  useRef, useEffect, useState, useContext,
} from 'react';
import { connect } from 'react-redux';
import track, { useTracking } from 'react-tracking';
import { useIntl } from 'gatsby-plugin-react-intl';

import { addTrackingToLink } from '@utils/analytics';
import { WebviewContext } from '@utils/context';
import { isEdxLink } from '@utils/edxLinks';
import { withI18nPrefix } from '@utils/i18n';
import { getWebviewUrl } from '@utils/program';

import { ProspectusLinkDefaultProps, ProspectusLinkPropType } from './types';

function BaseProspectusLink({
  allowFragment,
  analytics,
  children,
  className,
  href,
  linkProps,
  linkRef: suppliedLinkRef,
  userLanguage,
  displayUrlAsIs,
  onClick,
  onTrackingAdded,
  isTrackLink,
  allowWebviewLinksForEdxDomain,
}: ProspectusLinkPropType) {
  const intl = useIntl();
  const linkRef = suppliedLinkRef || useRef(null);
  const [rendered, setRendered] = useState(false);
  const webview = useContext(WebviewContext);

  const isCompleteUrl = /^(http|https|mailto):/.test(href);
  const isEdxUrl = !isCompleteUrl || isEdxLink(href);

  const tracking = useTracking();

  const getComponentProps = () => {
    const { linkProps: analyticsLinkProps = {} } = analytics;
    const componentProps = {
      category: 'navigation',
      link: linkRef.current.getAttribute('href'),
      ...analyticsLinkProps,
    };
    return componentProps;
  };

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  useEffect(() => {
    if (!rendered) {
      setRendered(true);
      return;
    }
    const { eventName } = analytics;

    if (isTrackLink) {
      addTrackingToLink(tracking, eventName, linkRef.current, getComponentProps);
      if (onTrackingAdded) {
        onTrackingAdded();
      }
    }
  }, [rendered, isTrackLink]);

  const getUrl = () => {
    // Allow the link to another section of the page.
    // The allowFragment property allows slowly introducing this to
    // the component without it affecting every usage.
    if (displayUrlAsIs || (allowFragment && href.startsWith('#'))) {
      return href;
    }

    if (!isCompleteUrl) {
      /**
       * If no userLanguage is set pass the language code for the page
       * to ensure links on /es pages point to /es pages
       */
      const languageCode = userLanguage || intl.locale;
      return withI18nPrefix(href, languageCode);
    }

    if (!isEdxUrl) {
      const delimiter = href.includes('?') ? '&' : '?';
      return `${href}${delimiter}external_link=true`;
    }

    return href;
  };

  const urlProp = {
    href: webview
    && (allowWebviewLinksForEdxDomain || !isCompleteUrl)
      ? getWebviewUrl(href) : getUrl(),
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <a
      className={className}
      {...linkProps}
      {...urlProp}
      target={!isEdxUrl ? '_blank' : undefined}
      rel={!isEdxUrl ? 'noopener noreferrer' : undefined}
      ref={linkRef}
      onClick={handleClick}
      onKeyDown={e => e}
    >
      {children}
    </a>
  );
}

const mapStateToProps = store => ({
  userLanguage: store.userLanguage,
});

BaseProspectusLink.defaultProps = ProspectusLinkDefaultProps;

const ProspectusLink = connect(mapStateToProps)(BaseProspectusLink);

export default track({
  component: 'prospectus-link',
})(ProspectusLink);
